// Config → Integrations. Admin-only. Connect the Front (frontapp.com) mailbox
// via a real OAuth consent flow, strictly read-only. Establishes the connection
// and shows connected / not-connected status. The read-only puller lives on the
// Sync Front button (POST /api/sync/front). No sending or writing to Front ever.
import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { Switch } from "../../components/ui/switch";
import api, { API } from "../../lib/api";
import { toast } from "sonner";
import { Plug, CheckCircle2, XCircle, ShieldCheck, Loader2, ExternalLink, MessageCircle, RefreshCw, Clock, ArrowUpFromLine } from "lucide-react";

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  // ── Front sync telemetry (last-sync + per-inbox cap) ──
  const [frontSync, setFrontSync] = useState(null);
  const [frontCap, setFrontCap] = useState(null);
  const [frontBusy, setFrontBusy] = useState(false);
  const frontUnmounted = useRef(false);
  useEffect(() => () => { frontUnmounted.current = true; }, []);

  // ── WhatsApp (Sendbee) read-only integration ──
  const [wa, setWa] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const waPoll = useRef(null);

  // ── Customer 360 CSV export settings ──
  const [exportCfg, setExportCfg] = useState(null);
  useEffect(() => {
    api.get("/crm/export-config").then(({ data }) => setExportCfg(data)).catch(() => {});
  }, []);
  const patchExportCfg = async (patch) => {
    try {
      const { data } = await api.patch("/crm/export-config", patch);
      setExportCfg(data);
      toast.success("Export settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/front/status");
      setStatus(data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWa = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/whatsapp/status");
      setWa(data);
      return data;
    } catch {
      setWa(null);
      return null;
    }
  }, []);

  useEffect(() => { load(); loadWa(); }, [load, loadWa]);

  // While a WhatsApp sync is running, poll status until it finishes.
  useEffect(() => {
    if (wa?.running && !waPoll.current) {
      waPoll.current = setInterval(async () => {
        const d = await loadWa();
        if (d && !d.running) { clearInterval(waPoll.current); waPoll.current = null; }
      }, 5000);
    }
    return () => { if (waPoll.current) { clearInterval(waPoll.current); waPoll.current = null; } };
  }, [wa?.running, loadWa]);

  const toggleWa = async (enabled) => {
    setWaBusy(true);
    try {
      const { data } = await api.post("/integrations/whatsapp/toggle", { enabled });
      setWa(data);
      toast.success(enabled ? "WhatsApp sync enabled" : "WhatsApp sync disabled");
    } catch (e) {
      toast.error("Could not update WhatsApp sync", { description: e?.response?.data?.detail || "" });
    } finally { setWaBusy(false); }
  };

  const syncWaNow = async () => {
    setWaBusy(true);
    try {
      const { data } = await api.post("/integrations/whatsapp/sync-now");
      if (data.status === "started") toast.success("WhatsApp sync started", { description: data.detail });
      else if (data.status === "already_running") toast.info("A sync is already running");
      else toast.info(data.detail || data.status);
      await loadWa();
    } catch (e) {
      toast.error("Sync failed", { description: e?.response?.data?.detail || "" });
    } finally { setWaBusy(false); }
  };

  const recheckWa = async () => {
    setWaBusy(true);
    try {
      const { data } = await api.post("/whatsapp/rematch");
      toast.success("Re-check complete", {
        description: `${data.checked} unmatched contact(s) checked · ${data.newly_matched} newly matched.`,
      });
    } catch (e) {
      toast.error("Re-check failed", { description: e?.response?.data?.detail || "" });
    } finally { setWaBusy(false); }
  };

  // Handle the callback bounce (?front=connected | error).
  useEffect(() => {
    const result = params.get("front");
    if (!result) return;
    if (result === "connected") toast.success("Front connected", { description: "Read-only access established." });
    else toast.error("Front connection failed", { description: params.get("reason") || "Please try again." });
    params.delete("front");
    params.delete("reason");
    setParams(params, { replace: true });
    load();
  }, [params, setParams, load]);

  const connect = () => {
    const token = localStorage.getItem("talmia_token") || "";
    // Full-page navigation → backend 302-redirects the browser to Front's consent screen.
    window.location.href = `${API}/integrations/front/authorize?token=${encodeURIComponent(token)}`;
  };

  const connected = !!status?.connected;
  const configured = !!status?.configured;

  const loadFrontSync = useCallback(async () => {
    try { const { data } = await api.get("/integrations/front/last-sync"); setFrontSync(data); }
    catch { setFrontSync(null); }
  }, []);
  const loadFrontCap = useCallback(async () => {
    try { const { data } = await api.get("/integrations/front/settings"); setFrontCap(data.per_inbox_cap); }
    catch { /* non-admins / not configured — leave as is */ }
  }, []);
  useEffect(() => {
    if (connected) { loadFrontSync(); loadFrontCap(); }
  }, [connected, loadFrontSync, loadFrontCap]);

  // Poll a background Front sync until it leaves "running". Tolerates brief
  // network/proxy blips; gives up after 5 failures in a row.
  const waitForFrontSync = async (syncId) => {
    let failures = 0;
    while (!frontUnmounted.current) {
      await new Promise((r) => setTimeout(r, 4000));
      if (frontUnmounted.current) break;
      try {
        const { data } = await api.get(`/sync/run/${syncId}`);
        failures = 0;
        if (data.puller_status !== "running") return data;
      } catch (e) {
        if (++failures >= 5) {
          toast.error("Lost contact with the Front sync", {
            description: "It may still be running on the server. Refresh in a few minutes to see the result.",
          });
          return null;
        }
      }
    }
    return null;
  };

  // Sync Front. When bumpCap is true ("Pull more"), raise the per-inbox cap by
  // 300 first, then run the normal sync. All toasts handled here so a cap hit
  // reads as amber info, not an error.
  const syncFront = async (bumpCap = false) => {
    setFrontBusy(true);
    try {
      if (bumpCap) {
        const next = (frontCap ?? 300) + 300;
        const { data } = await api.post("/integrations/front/settings", { per_inbox_cap: next });
        setFrontCap(data.per_inbox_cap);
      }
      // Full pulls outlast the proxy's 120s timeout, so start in the background
      // and poll for the result. (An older backend ignores the flag and answers
      // with the finished result, which falls straight through below.)
      const { data: started } = await api.post("/sync/front", null, { params: { background: true } });
      let data = started;
      if (started.puller_status === "running" && started.sync_id) {
        data = await waitForFrontSync(started.sync_id);
        if (!data) return;  // page left, or contact lost (toast already shown)
      }
      const ps = data.puller_status;
      if (ps === "not_connected") {
        toast.error("Front not connected", { description: data.note });
      } else if (ps === "skipped") {
        toast.info("A Front sync is already running");
      } else if (ps === "error") {
        toast.error("Front sync failed", { description: data.note });
      } else if (data.cap_reached) {
        const names = (data.inboxes || []).filter((i) => i.cap_reached).map((i) => i.inbox_name).join(", ") || "one or more inboxes";
        toast.warning(`Synced ${data.synced} emails · cap reached on ${names} — use Pull more to fetch older conversations`);
      } else {
        toast.success(`Synced ${data.synced} new email${data.synced === 1 ? "" : "s"}`, { description: data.note });
      }
      await loadFrontSync();
      await loadFrontCap();
    } catch (e) {
      toast.error("Front sync failed", { description: e?.response?.data?.detail || "" });
    } finally { setFrontBusy(false); }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-8 px-4" data-testid="integrations-page">
        <div className="flex items-center gap-2.5 mb-1.5">
          <Plug className="w-6 h-6 text-teal-400" />
          <h1 className="text-2xl font-bold text-white tracking-tight">Integrations</h1>
        </div>
        <p className="text-sm text-slate-400 mb-6 max-w-2xl leading-relaxed">
          Connect external data sources. All connections here are{" "}
          <b className="text-slate-200">strictly read-only</b> — Talmia never sends,
          replies to, or modifies anything in the connected account.
        </p>

        {/* Front card */}
        <div className="rounded-xl border border-white/10 bg-[#0E1524] p-6" data-testid="front-integration-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-white">Front</h2>
                {loading ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-400" data-testid="front-status-loading">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> checking…
                  </span>
                ) : connected ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium" data-testid="front-status-connected">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 text-xs font-medium" data-testid="front-status-disconnected">
                    <XCircle className="w-3.5 h-3.5" /> Not connected
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-400 mt-1.5 max-w-xl leading-relaxed">
                Pull inboxes, conversations and messages into Customer 360. Requests
                only read scopes: <span className="font-mono text-[11px] text-slate-300">inboxes:read · conversations:read · messages:read</span>.
              </p>
              <div className="flex items-center gap-1.5 text-[11px] text-teal-300/80 mt-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Read-only — no sending or writing to Front.
              </div>
              {connected && status?.account && (
                <p className="text-[11px] text-emerald-300/90 mt-2" data-testid="front-account">
                  Connected as {status.account}
                </p>
              )}
              {!connected && configured && status?.detail && (
                <p className="text-[11px] text-red-400 mt-2 max-w-xl leading-relaxed" data-testid="front-error-detail">
                  {status.detail}
                </p>
              )}
              {connected && status?.updated_at && (
                <p className="text-[11px] text-slate-500 mt-2" data-testid="front-connected-since">
                  Connected {new Date(status.updated_at).toLocaleString("en-GB")}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5 pt-5 border-t border-white/5">
            {!configured ? (
              <span className="text-xs text-amber-300/90" data-testid="front-not-configured">
                Front OAuth client not configured yet — add FRONT_CLIENT_ID / FRONT_CLIENT_SECRET.
              </span>
            ) : (
              <button
                onClick={connect}
                data-testid="connect-front-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-teal-500 hover:bg-teal-400 text-black transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {connected ? "Reconnect Front" : "Connect Front"}
              </button>
            )}
            {connected && (
              <button
                onClick={() => syncFront(false)}
                disabled={frontBusy}
                data-testid="front-sync-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${frontBusy ? "animate-spin" : ""}`} />
                {frontBusy ? "Syncing…" : "Sync Front"}
              </button>
            )}
          </div>

          {/* Last Front sync — totals + per-inbox counts + per-inbox cap + Pull more */}
          {connected && (
            <div className="mt-5 pt-5 border-t border-white/5" data-testid="front-last-sync">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2 text-[11px] text-slate-400 font-mono">
                  <Clock className="w-3.5 h-3.5" />
                  {frontSync?.found ? (
                    <span data-testid="front-last-sync-summary">
                      Last sync {new Date(frontSync.finished_at || frontSync.started_at).toLocaleString("en-GB")} ·{" "}
                      <span className="text-slate-200">{frontSync.synced} new</span>
                    </span>
                  ) : (
                    <span data-testid="front-last-sync-none">No Front sync yet.</span>
                  )}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-[11px] text-slate-500 font-mono" data-testid="front-cap">
                    Per-inbox cap: <span className="text-slate-200">{frontCap ?? "…"}</span>
                  </span>
                  <button
                    onClick={() => syncFront(true)}
                    disabled={frontBusy}
                    data-testid="front-pull-more-btn"
                    title="Raise the per-inbox cap by 300 and sync again to fetch older conversations"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                  >
                    <ArrowUpFromLine className="w-3 h-3" /> Pull more
                  </button>
                </div>
              </div>
              {frontSync?.found && (frontSync.inboxes || []).length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
                  <table className="w-full text-[11px]" data-testid="front-inbox-table">
                    <thead className="bg-white/[0.03] text-slate-400 font-mono uppercase tracking-wider text-[9.5px]">
                      <tr>
                        <th className="text-left px-3 py-1.5">Inbox</th>
                        <th className="text-right px-3 py-1.5">Scanned</th>
                        <th className="text-right px-3 py-1.5">New</th>
                        <th className="text-right px-3 py-1.5">Skipped</th>
                        <th className="px-3 py-1.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {frontSync.inboxes.map((ib) => (
                        <tr key={ib.inbox_id} className="border-t border-white/[0.04]" data-testid={`front-inbox-row-${ib.inbox_id}`}>
                          <td className="px-3 py-1.5 text-slate-200">{ib.inbox_name || ib.inbox_id}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-300">{ib.scanned}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-emerald-300">{ib.new}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">{ib.skipped_existing}</td>
                          <td className="px-3 py-1.5 text-right">
                            {ib.cap_reached && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 font-mono text-[9.5px] uppercase tracking-wider" data-testid={`front-cap-badge-${ib.inbox_id}`}>
                                More to pull
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* WhatsApp (Sendbee) card */}
        <div className="rounded-xl border border-white/10 bg-[#0E1524] p-6 mt-6" data-testid="whatsapp-integration-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-white">WhatsApp</h2>
                {!wa ? null : !wa.configured ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-xs font-medium" data-testid="whatsapp-status-not-configured">
                    <XCircle className="w-3.5 h-3.5" /> Not configured
                  </span>
                ) : wa.enabled ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-medium" data-testid="whatsapp-status-enabled">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 text-xs font-medium" data-testid="whatsapp-status-disabled">
                    <XCircle className="w-3.5 h-3.5" /> Disabled
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-400 mt-1.5 max-w-xl leading-relaxed">
                Pulls WhatsApp message history (Sendbee) and matches it to customers by
                phone number, onto Customer 360. Runs automatically once a day when enabled.
              </p>
              <div className="flex items-center gap-1.5 text-[11px] text-green-300/80 mt-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Read-only — Talmia never sends WhatsApp messages.
              </div>
              {wa?.last_run && (
                <p className="text-[11px] text-slate-500 mt-2" data-testid="whatsapp-last-run">
                  Last run: {wa.last_run.status}
                  {typeof wa.last_run.synced === "number" && ` · ${wa.last_run.synced} synced`}
                  {typeof wa.last_run.matched === "number" && ` · ${wa.last_run.matched} matched`}
                  {wa.last_run.finished_at && ` · ${new Date(wa.last_run.finished_at).toLocaleString("en-GB")}`}
                </p>
              )}
            </div>
            {wa?.configured && (
              <label className="flex items-center gap-2 shrink-0" title="Daily sync on/off">
                <span className="text-xs text-slate-400">Daily sync</span>
                <Switch
                  checked={!!wa?.enabled}
                  disabled={waBusy}
                  onCheckedChange={toggleWa}
                  data-testid="whatsapp-toggle"
                />
              </label>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5 pt-5 border-t border-white/5">
            {!wa?.configured ? (
              <span className="text-xs text-amber-300/90" data-testid="whatsapp-not-configured-note">
                Sendbee keys not configured — add SENDBEE_PUBLIC_KEY / SENDBEE_SECRET_KEY.
              </span>
            ) : (
              <button
                onClick={syncWaNow}
                disabled={waBusy || wa?.running || !wa?.enabled}
                data-testid="whatsapp-sync-now-btn"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  wa?.enabled && !wa?.running
                    ? "bg-green-500 hover:bg-green-400 text-black"
                    : "bg-white/5 text-slate-500 cursor-not-allowed"
                }`}
              >
                <RefreshCw className={`w-4 h-4 ${wa?.running ? "animate-spin" : ""}`} />
                {wa?.running ? "Syncing…" : "Sync now"}
              </button>
            )}
            {wa?.configured && (
              <button
                onClick={recheckWa}
                disabled={waBusy || wa?.running}
                data-testid="whatsapp-recheck-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors border border-white/15 text-slate-200 hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Re-run the phone match for every unmatched contact"
              >
                <RefreshCw className="w-4 h-4" /> Re-check unmatched
              </button>
            )}
            {wa?.configured && !wa?.enabled && (
              <span className="text-[11px] text-slate-500">Enable the daily sync to run a manual sync.</span>
            )}
          </div>
        </div>
      </div>

      {/* Customer 360 CSV export */}
      <div className="max-w-4xl mx-auto px-4 pb-10">
        <div className="rounded-xl border border-white/10 bg-[#0E1524] p-6" data-testid="export-config-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/15 border border-indigo-500/30 grid place-items-center">
              <MessageCircle className="w-4 h-4 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Customer data export</h2>
              <p className="text-[12px] text-slate-400">Download Customer 360 data as tidy CSVs for offline / AI analysis.</p>
            </div>
          </div>
          {exportCfg && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <label className="text-[12px] text-slate-300 w-40">Default export level</label>
                <select value={exportCfg.default_level}
                  onChange={(e) => patchExportCfg({ default_level: e.target.value })}
                  data-testid="export-default-level"
                  className="h-9 px-3 rounded-lg bg-black/30 border border-white/10 text-[13px] text-white outline-none focus:border-indigo-400/60">
                  <option value="basic">Basic (one row per customer)</option>
                  <option value="advanced">Advanced (ZIP of orders/quotes/calls/whatsapp)</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-[12px] text-slate-300 w-40">Allow "export all"</label>
                <Switch checked={!!exportCfg.allow_all_export}
                  onCheckedChange={(v) => patchExportCfg({ allow_all_export: v })}
                  data-testid="export-allow-all" />
                <span className="text-[11px] text-slate-500">Let admins export every customer at once.</span>
              </div>
              {exportCfg.allow_all_export && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <a href={`${API}/crm/export/customers.csv?level=basic`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-[12px] text-slate-200 hover:bg-white/5"
                    data-testid="export-all-basic">Export all · Basic CSV</a>
                  <a href={`${API}/crm/export/customers.csv?level=advanced`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-[12px] text-slate-200 hover:bg-white/5"
                    data-testid="export-all-advanced">Export all · Advanced ZIP</a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
